import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '../api';

const AuthContext = createContext(null);
const SESSION_HOURS = 18;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [textScale, setTextScale] = useState(() => {
    const stored = localStorage.getItem('user');
    if (stored) { try { return JSON.parse(stored).text_scale || 1.0; } catch(e) {} }
    return 1.0;
  });
  const expiryTimer = useRef(null);

  // Session expiry check
  const startExpiryTimer = useCallback(() => {
    if (expiryTimer.current) clearTimeout(expiryTimer.current);
    const loginTime = localStorage.getItem('loginTime');
    if (!loginTime) return;
    const elapsed = Date.now() - parseInt(loginTime);
    const remaining = (SESSION_HOURS * 60 * 60 * 1000) - elapsed;
    if (remaining <= 0) {
      // Session expired
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('loginTime');
      setToken(null); setUser(null); setTextScale(1.0);
      return;
    }
    expiryTimer.current = setTimeout(() => {
      try { api.post('/auth/logout').catch(() => {}); } catch(e) {}
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('loginTime');
      setToken(null); setUser(null); setTextScale(1.0);
      window.location.reload();
    }, remaining);
  }, []);

  useEffect(() => {
    if (token) startExpiryTimer();
    return () => { if (expiryTimer.current) clearTimeout(expiryTimer.current); };
  }, [token, startExpiryTimer]);

  const login = useCallback(async (email, password, mfaCode) => {
    const payload = { email, password };
    if (mfaCode) payload.mfa_code = mfaCode;
    const res = await api.post('/auth/login', payload);
    // MFA required — partial response
    if (res.status === 206 || res.data.mfa_required) {
      return { mfa_required: true };
    }
    const { data } = res;
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('loginTime', String(Date.now()));
    setToken(data.token);
    setUser(data.user);
    setTextScale(data.user.text_scale || 1.0);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch(e) {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('loginTime');
    setToken(null); setUser(null); setTextScale(1.0);
  }, []);

  const updateTextScale = useCallback((scale) => {
    setTextScale(scale);
    const stored = localStorage.getItem('user');
    if (stored) {
      try { const u = JSON.parse(stored); u.text_scale = scale; localStorage.setItem('user', JSON.stringify(u)); setUser(u); } catch(e) {}
    }
  }, []);

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated, textScale, updateTextScale }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
